/**
 * The Investor Portals view's rules (#11635 Round 4, documents-ui spec R17 to
 * R20): how a service reads in the overview, how the overview sorts and sums,
 * and how the portal documents group by manager. Pure functions over the
 * services read and the search rows, so every rule is unit-tested against a
 * fixed clock and the components only draw.
 */
import type {
  PortalCoverage,
  PortalService,
  PortalServiceState,
} from "@/components/FpFundBrain/Manage/Connections/InvestorPortalsRow";
import { formatRelativeTemporal } from "@/components/FpFundBrain/Manage/Connections/relativeTime";
import type { Temporal } from "@/utils/temporal";
import { formatInstant, formatTemporal } from "@/utils/temporal";
import {
  PORTAL_ACTIVITY_WINDOW_DAYS,
  historyText,
} from "@/components/FpFundBrain/Manage/Connections/InvestorPortalsRow";
import type { DocumentRow, LinkedEntity } from "./api";
import { statusBucketOf } from "./rows";
import type { PortalMail } from "../portalMail";

/** The uploader key a service's documents carry, from the service's name
 *  (spec R11 / R22; Round 7, R22: a service's page is scoped by it). */
export const serviceKeyOf = (name: string): string => `system:${name}`;

/** The uploader key a service's documents carry (spec R11 / R22). */
export const serviceUploaderKey = (service: PortalService): string =>
  serviceKeyOf(service.service);

/** How the overview buckets a service (spec R19), in display order. */
export type ServiceBucket =
  | "needs_attention"
  | "received"
  | "quiet"
  | "enrolling";

const BUCKET_ORDER: Record<ServiceBucket, number> = {
  needs_attention: 0,
  received: 1,
  quiet: 2,
  enrolling: 3,
};

/** A service's bucket, from its state (Round 19, R65; kept at the rebase
 *  onto #13095 step 3b): the one derivation the lede, the order and every
 *  row's Status label read, so they cannot disagree. The stage stays on the
 *  wire for an engineer and drives nothing here. */
export const bucketOf = (service: PortalService): ServiceBucket => {
  const state: PortalServiceState = service.state;
  switch (state) {
    case "needs_attention":
      return "needs_attention";
    case "enrolling":
      return "enrolling";
    case "connected":
      return service.recent_document_count > 0 ? "received" : "quiet";
    default: {
      const unhandled: never = state;
      throw new Error(`unhandled service state: ${JSON.stringify(unhandled)}`);
    }
  }
};

/** A wire temporal's instant as epoch milliseconds, for ordering; a
 *  calendar day sorts at its UTC midnight (the only reading it has), and a
 *  missing value at the far past so it sorts last in a newest-first order. */
export const epochOf = (value: Temporal | null): number => {
  if (value === null) return Number.NEGATIVE_INFINITY;
  switch (value.granularity) {
    case "instant":
      return Date.parse(value.at);
    case "date":
      return Date.parse(`${value.day}T00:00:00Z`);
    default: {
      const unhandled: never = value;
      throw new Error(
        `unhandled temporal granularity: ${JSON.stringify(unhandled)}`
      );
    }
  }
};

/** The instant a bucket sorts its rows by, newest first: the last document
 *  for a service that has or had documents, the state's own instant for the
 *  other two. */
const sortInstant = (service: PortalService): number => {
  switch (bucketOf(service)) {
    case "received":
    case "quiet":
      return epochOf(service.last_document_at);
    case "needs_attention":
    case "enrolling":
      return epochOf(service.state_since);
  }
};

/** The overview's order (spec R19): needs attention, received, nothing new,
 *  enrolling; within a bucket the most recent instant first, services with
 *  none last, then by name. */
export const sortServices = (services: PortalService[]): PortalService[] =>
  [...services].sort((a, b) => {
    const bucket = BUCKET_ORDER[bucketOf(a)] - BUCKET_ORDER[bucketOf(b)];
    if (bucket !== 0) return bucket;
    const byInstant = sortInstant(b) - sortInstant(a);
    if (byInstant !== 0 && !Number.isNaN(byInstant)) return byInstant;
    return a.service.localeCompare(b.service);
  });

/** One state's services, as the Services band groups them (slice 1). */
export interface ServiceGroup {
  state: PortalServiceState;
  services: PortalService[];
}

const GROUP_ORDER: PortalServiceState[] = [
  "needs_attention",
  "enrolling",
  "connected",
];

/** The overview's groups (slice 1, R1): needs attention, enrolling,
 *  connected, each only when it holds a service; within a group the
 *  overview's order, so Connected lists the services that received
 *  documents before the quiet ones. */
export const groupServices = (services: PortalService[]): ServiceGroup[] => {
  const sorted = sortServices(services);
  return GROUP_ORDER.map((state) => ({
    state,
    services: sorted.filter((s) => s.state === state),
  })).filter((group) => group.services.length > 0);
};

const STATE_ORDER: Record<PortalServiceState, number> = {
  needs_attention: 0,
  enrolling: 1,
  connected: 2,
};

/** The column's order for the services under the Investor Portals row: the
 *  Connectors row's (spec R14) — needs attention, enrolling, connected; the
 *  most recent state instant (the last sync for connected) first, services
 *  with none last, then by name. */
export const sortServicesForColumn = (
  services: PortalService[]
): PortalService[] =>
  [...services].sort((a, b) => {
    const state = STATE_ORDER[a.state] - STATE_ORDER[b.state];
    if (state !== 0) return state;
    const instantOf = (s: PortalService) =>
      epochOf(s.state === "connected" ? s.last_sync_at : s.state_since);
    const byInstant = instantOf(b) - instantOf(a);
    if (byInstant !== 0 && !Number.isNaN(byInstant)) return byInstant;
    return a.service.localeCompare(b.service);
  });

const plural = (count: number, noun: string): string =>
  `${count} ${noun}${count === 1 ? "" : "s"}`;

/** A wire temporal as "2h ago" (instant) or the day itself (date); a dash
 *  for none. The overview's Last sync cell and the status texts share it. */
export const relativeOf = (value: Temporal | null): string =>
  value === null ? "—" : formatRelativeTemporal(value);

/** A wire temporal as its month and day in the viewer's zone; a dash for
 *  none. The overview's Last document cell and the status texts share it. */
export const monthDayOf = (value: Temporal | null, zone: string): string => {
  if (value === null) return "—";
  switch (value.granularity) {
    case "instant":
      return formatInstant(value.at, zone, "monthDay");
    case "date":
      return value.day;
    default: {
      const unhandled: never = value;
      throw new Error(
        `unhandled temporal granularity: ${JSON.stringify(unhandled)}`
      );
    }
  }
};

/** The overview's lede (spec R19): the window, then its totals with zero
 *  clauses left out — "Last 7 days · 3 documents from 1 service · …" — or
 *  "No documents in the last 7 days · …" when nothing arrived. Connections
 *  sharing a service name share one upload string, so each service key is
 *  counted once (spec R21). Returned as its clauses, in order, so the band
 *  keeps each one whole on a narrow frame (Round 17, R63); joined with
 *  " · " they are the lede. */
export const overviewLedeClauses = (services: PortalService[]): string[] => {
  const byKey = new Map<string, PortalService>();
  for (const service of services)
    if (!byKey.has(serviceUploaderKey(service)))
      byKey.set(serviceUploaderKey(service), service);
  const distinct = [...byKey.values()];
  if (distinct.length === 0) return ["No services"];
  const received = distinct.filter((s) => bucketOf(s) === "received");
  const documents = received.reduce(
    (sum, s) => sum + s.recent_document_count,
    0
  );
  const quiet = distinct.filter((s) => bucketOf(s) === "quiet").length;
  const attention = distinct.filter(
    (s) => bucketOf(s) === "needs_attention"
  ).length;
  const enrolling = distinct.filter((s) => bucketOf(s) === "enrolling").length;
  const parts: string[] =
    documents > 0
      ? [
          `Last ${PORTAL_ACTIVITY_WINDOW_DAYS} days`,
          `${plural(documents, "document")} from ${plural(received.length, "service")}`,
        ]
      : [`No documents in the last ${PORTAL_ACTIVITY_WINDOW_DAYS} days`];
  if (quiet > 0) parts.push(`${quiet} with nothing new`);
  if (attention > 0)
    parts.push(`${attention} need${attention === 1 ? "s" : ""} attention`);
  if (enrolling > 0) parts.push(`${enrolling} enrolling`);
  return parts;
};

/** The services a Source pick names, or all of them with no pick. Picks
 *  that name no service (a connector's key) narrow to nothing. */
export const pickedServices = (
  services: PortalService[],
  sourceKeys: string[]
): PortalService[] =>
  sourceKeys.length === 0
    ? services
    : services.filter((s) => sourceKeys.includes(serviceUploaderKey(s)));

// --- the documents half --------------------------------------------------

/** What a portal document groups under (spec R20): the first manager it is
 *  linked to; failing that, the folder it sits in; a row still processing
 *  with neither sits under Processing. */
export type RowGroup =
  | { kind: "record"; key: string; label: string; record: LinkedEntity }
  | { kind: "folder"; key: string; label: string }
  | { kind: "processing"; key: "processing"; label: "Processing" };

export const groupOf = (row: DocumentRow): RowGroup => {
  const manager = row.linkedEntities.find((e) => e.kind === "manager");
  if (manager)
    return {
      kind: "record",
      key: `record:${manager.publicId}`,
      label: manager.name,
      record: manager,
    };
  if (row.filePath.length > 0) {
    const folder = row.filePath[row.filePath.length - 1] ?? "";
    return {
      kind: "folder",
      key: `folder:${JSON.stringify([row.driveId, row.filePath])}`,
      label: folder,
    };
  }
  if (statusBucketOf(row) === "processing")
    return { kind: "processing", key: "processing", label: "Processing" };
  return { kind: "folder", key: "folder:root", label: "Documents" };
};

/** The rows reordered so each group's rows sit together (spec R20): groups
 *  in the order their newest row appears, Processing first; rows keep their
 *  own order within a group. The stream is newest-first already, so first
 *  appearance is newest document. */
export const groupRows = (rows: DocumentRow[]): DocumentRow[] => {
  const order: string[] = [];
  const byGroup = new Map<string, DocumentRow[]>();
  for (const row of rows) {
    const group = groupOf(row);
    const bucket = byGroup.get(group.key);
    if (bucket) bucket.push(row);
    else {
      byGroup.set(group.key, [row]);
      order.push(group.key);
    }
  }
  const processingFirst = [
    ...order.filter((key) => key === "processing"),
    ...order.filter((key) => key !== "processing"),
  ];
  return processingFirst.flatMap((key) => byGroup.get(key) ?? []);
};

// --- #13095 steps 3b and 3c: the coverage and the history floor -----------
// Read on the service page's facts line (Rebase, Oct 13: no tile carries a
// stage or a coverage line).

/** "412 of 412", "230 of 1000+", or "412 held" when the portal shows no
 *  total. Never more held than shown: a total read on a filtered view, or
 *  a document held that the portal has since taken down, reads as all of
 *  it. */
const heldOf = (coverage: PortalCoverage): string => {
  if (coverage.total === null) return `${coverage.held} held`;
  const total = `${coverage.total}${coverage.total_is_floor ? "+" : ""}`;
  return `${Math.min(coverage.held, coverage.total)} of ${total}`;
};

const yearOf = (value: Temporal): string =>
  formatTemporal(value, "isoMonth").slice(0, 4);

/** How much of the service's history we hold: "Mar 2014 to Oct 2026, 412
 *  of 412" once the walk reached the start; "Covers 2019 to 2026 so far,
 *  230 of 412" while it is still walking back (#13095 step 7, R-P4: there
 *  is no Backfilling). Null with no record. */
export const coverageText = (
  coverage: PortalCoverage | null
): string | null => {
  if (coverage === null) return null;
  const { earliest, latest } = coverage;
  if (!coverage.reached_start) {
    const span =
      earliest && latest
        ? `${yearOf(earliest)} to ${yearOf(latest)} so far, `
        : "";
    return `Covers ${span}${heldOf(coverage)}`;
  }
  const span =
    earliest && latest
      ? `${formatTemporal(earliest, "monthYear")} to ${formatTemporal(latest, "monthYear")}, `
      : "";
  const proof = coverage.unverifiable ? " (start not provable)" : "";
  return `${span}${heldOf(coverage)}${proof}`;
};

/** The facts line's history clauses (#13095 3b and 3c): how much of the
 *  history is held, when a walk recorded it, then how far back it is kept
 *  ("History from Oct 2025", "All history"). */
export const historyClauses = (service: PortalService): string[] => {
  const covered = coverageText(service.coverage);
  return [...(covered === null ? [] : [covered]), historyText(service)];
};

/** Whether a service's page and list say what its syncs gave up on
 *  (#13095 R-R1b): something was given up on, and the service does not need
 *  attention, which says only that (R68). */
const saysGivenUp = (service: PortalService): boolean =>
  service.given_up > 0 && service.state !== "needs_attention";

/** The list's clause for what a connected service's syncs gave up on
 *  ("2 could not be fetched"), or null. */
export const givenUpClause = (service: PortalService): string | null =>
  saysGivenUp(service) && service.state === "connected"
    ? `${service.given_up.toLocaleString()} could not be fetched`
    : null;

/** The service page's sentence for what its syncs gave up on (#13095
 *  R-R1b): how many documents, then each kind with its count. Null when
 *  there is nothing to say. */
export const givenUpText = (service: PortalService): string | null => {
  if (!saysGivenUp(service)) return null;
  const count = service.given_up;
  const kinds = service.given_up_reasons
    .map((reason) => `${reason.reason_text} (${reason.count.toLocaleString()})`)
    .join(", ");
  const head = `${count.toLocaleString()} ${
    count === 1 ? "document" : "documents"
  } could not be fetched after repeated tries`;
  return kinds ? `${head}: ${kinds}.` : `${head}.`;
};

// --- Round 5: the tiles, the card and the mail lede ----------------------

/** A tile's last line (R1, slice 1): when the service last synced. */
export const lastSyncText = (service: PortalService): string =>
  service.last_sync_at
    ? `Last sync ${relativeOf(service.last_sync_at)}`
    : "No sync yet";

/** The state a tile's pill shows (slice 1 Round 3, R10/R11; #13095 step 7,
 *  R-P4): Needs attention whatever else is true, else Syncing while a run
 *  is going, else the service's state (Synced for `connected`). */
export type PillState = PortalServiceState | "syncing";

/** Whether a sync is running for the service now (R10). */
export const isSyncing = (service: PortalService): boolean =>
  service.syncing_since !== null;

export const pillStateOf = (service: PortalService): PillState =>
  service.state !== "needs_attention" && isSyncing(service)
    ? "syncing"
    : service.state;

/** The window's document count, short (R11): "3 docs in 7d", or "no docs in
 *  7d". The window is the one the backend counts over. */
export const recentDocsText = (count: number): string => {
  const window = `${PORTAL_ACTIVITY_WINDOW_DAYS}d`;
  if (count === 0) return `no docs in ${window}`;
  return `${count} ${count === 1 ? "doc" : "docs"} in ${window}`;
};

/** A tile's one metadata line (R10, R11), as its clauses: the run while one
 *  is going; for a connected service its last sync and the window's count;
 *  for a state that needs a look, since when. The tile joins them with
 *  " · " on one line, truncated at the tile's edge (R36). */
export const tileMetaText = (service: PortalService): string[] => {
  const syncingSince = service.syncing_since;
  if (syncingSince !== null)
    return ["Syncing now", `since ${relativeOf(syncingSince)}`];
  switch (service.state) {
    case "connected":
      return service.last_sync_at
        ? [lastSyncText(service), recentDocsText(service.recent_document_count)]
        : ["No sync yet"];
    case "needs_attention":
    case "enrolling":
      return [
        service.state_since
          ? `Since ${relativeOf(service.state_since)}`
          : lastSyncText(service),
      ];
  }
};

/** A service's sign-ins (R12), after the "Sign-ins:" label on its page's
 *  header line, so the label is not repeated. */
export const signInsText = (count: number): string => {
  if (count === 0) return "none in the last 30 days";
  if (count === 1) return "once in the last 30 days";
  return `${count} times in the last 30 days`;
};

const DAYS_30 = 30 * 24 * 3_600_000;

/** How many of a service's code mails a run used in the last thirty days
 *  (R12), from the mail read. */
export const signInsOf = (
  mails: PortalMail[],
  connectionId: number,
  now: number
): number =>
  mails.filter(
    (mail) =>
      mail.connection_id === connectionId &&
      mail.action.kind === "signed_in" &&
      now - epochOf(mail.received_at) <= DAYS_30
  ).length;

// --- a service's page (Round 8, R26) ---------------------------------------

/** One card in a service's Managers section (Round 9, R28). */
export interface ManagerCard {
  name: string;
  /** The manager's record, to open; null for a name with none. */
  publicId: string | null;
  /** The record's kind, for opening it. */
  kind: string;
  /** Documents that arrived through the service linked to the manager. */
  documentCount: number;
}

const sameName = (a: string, b: string): boolean =>
  a.trim().toLocaleLowerCase() === b.trim().toLocaleLowerCase();

/** A service's managers (R28): the managers its documents are linked to, in
 *  the read's order (most documents first), then the record linked at setup
 *  when it is a manager and not already among them (a record of another
 *  kind, a vehicle, is not a manager card). The typed `managers` list is
 *  never a card: it holds whatever was typed at setup, and nothing ties it
 *  to a record. A row from before the kind was stored reads as a manager. */
export const serviceManagers = (service: PortalService): ManagerCard[] => {
  const cards: ManagerCard[] = service.delivered_managers.map((manager) => ({
    name: manager.name,
    publicId: manager.public_id,
    kind: "manager",
    documentCount: manager.document_count,
  }));
  const linkedName = service.entity_name?.trim() ?? "";
  const linkedId = service.entity_id;
  if (
    linkedId !== null &&
    linkedName !== "" &&
    (service.entity_kind ?? "manager") === "manager" &&
    !cards.some(
      (card) => card.publicId === linkedId || sameName(card.name, linkedName)
    )
  )
    cards.push({
      name: linkedName,
      publicId: linkedId,
      kind: "manager",
      documentCount: 0,
    });
  return cards;
};

// --- Round 17: the list view and the stored choice -------------------------

/** Where a browser keeps the Services band's view (Round 17, R63). */
export const SERVICES_VIEW_KEY = "fp-portals-services-view";

/** The Services band's two views (R63): the list, the default, and the
 *  cards. */
export type ServicesView = "list" | "cards";

/** A stored view as the band reads it: only the exact "cards" is the cards;
 *  anything else, or nothing, is the list. */
export const parseServicesView = (raw: string | null): ServicesView =>
  raw === "cards" ? "cards" : "list";

/** The view this browser last picked; the list when it picked none or
 *  refuses storage. */
export const readServicesView = (): ServicesView => {
  try {
    return parseServicesView(localStorage.getItem(SERVICES_VIEW_KEY));
  } catch {
    return "list";
  }
};

export const writeServicesView = (view: ServicesView): void => {
  try {
    localStorage.setItem(SERVICES_VIEW_KEY, view);
  } catch {
    // A browser that refuses storage just forgets.
  }
};

/** The since clause after a list row's state label (R65, amended), on the
 *  same line: since when a service has needed attention or been enrolling;
 *  null for a connected service or
 *  one with no state instant. Never the reason: the overview states none,
 *  in either view (R54); the service's page does. */
export const listRowSince = (service: PortalService): string | null => {
  if (service.state === "connected" || service.state_since === null)
    return null;
  return `since ${relativeOf(service.state_since)}`;
};
